import type { Location } from 'react-router-dom';

/** Router state carried on a link into a player profile, so the profile can
 *  offer a way back to the exact page the player came from — the hard
 *  leaderboard, or June's season, not a default. */
export interface FromState {
  from?: unknown;
}

/** Pages a profile may send someone back to. Anything else is ignored.
 *
 *  `from` arrives as router state, which only our own code sets — but it
 *  survives history entries and reloads, so it is treated as untrusted
 *  input rather than assumed well-formed. Restricting to known in-app
 *  prefixes also means a protocol-relative value like `//evil.example`
 *  can never become a navigation target. */
const ALLOWED_PREFIXES = ['/leaderboard', '/season'];

/** The current page expressed as the value to hand forward as `from`. */
export function originOf(location: Pick<Location, 'pathname' | 'search'>): string {
  return `${location.pathname}${location.search}`;
}

/** A safe in-app path to return to, or null when there isn't a usable one
 *  (a profile opened cold — deep link, refresh, new tab). */
export function backTarget(state: unknown): string | null {
  const raw = (state as FromState | null)?.from;
  if (typeof raw !== 'string') return null;
  // Single leading slash only: `//host` is protocol-relative and leaves the site.
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  const path = raw.split('?')[0] ?? '';
  return ALLOWED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`)) ? raw : null;
}

/** What to call the place they came from. */
export function backLabel(target: string): string {
  return target.startsWith('/season') ? 'Back to season' : 'Back to leaderboard';
}
